import React from 'react';
import cn from 'classnames';

import { Icon } from 'components/shared/Icon';
import { Typography } from 'components/shared/Typography';
import { Button } from 'components/shared/Button';

import { Tooltip } from 'components/Tooltip';

import { CoursePreview } from 'components/CoursePreview';
import { SchoolPreview } from 'components/SchoolPreview';

import { Cell } from 'components/shared/Table/components/Cell';

import cellStyles from 'components/shared/Table/components/Cell/Cell.module.scss';

import type { LessonGroup } from '../../CoursesLessonsPage.grouping';

import styles from './CoursesLessonsGroupedTable.module.scss';

type TableColumn = {
  key: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  justifyContent?: string;
};

type CellRendererProps = {
  item: any;
  key: string;
  minWidth?: number;
  maxWidth?: number;
  itemIndex?: number;
  className?: string;
};

type TableAction = {
  key: string;
  icon: string;
  tooltipText: string;
  onClick: (item: any) => void;
};

const CoursesLessonsGroupedTable = ({
  columns,
  params,
  lessonGroups,
  showGroupHeaders,
  actions = [],
  cellRenderer,
  onSortChange,
  onRowClick,
  isGroupExpanded,
  onGroupHeaderClick,
}: {
  columns: TableColumn[];
  params: Record<string, any>;
  lessonGroups: LessonGroup[];
  showGroupHeaders: boolean;
  actions?: TableAction[];
  cellRenderer: (props: CellRendererProps, DefaultCell: typeof Cell) => React.ReactNode;
  onSortChange: (sortKey: string) => void;
  onRowClick: (item: any) => void;
  isGroupExpanded: (groupId: string) => boolean;
  onGroupHeaderClick: (groupId: string) => void;
}) => (
  <div className={styles.table}>
    <div className={styles.header}>
      {columns.map((column) => (
        <div
          key={column.key}
          className={cn(cellStyles.cell, {
            [styles.sortableCell]: onSortChange,
          })}
          style={{
            minWidth: column.minWidth,
            maxWidth: column.maxWidth,
            justifyContent: column.justifyContent,
          }}
          onClick={() => {
            if (column.sortable) {
              onSortChange(column.key);
            }
          }}
        >
          <Typography
            variant="body3"
            weight="medium"
            mode="compact"
            className={styles.headerText}
          >
            {column.label}
          </Typography>
          {column.key === params.sortBy && (
            <Icon
              name={
                params.sortDirection === 'asc' ? 'arrowSortUp' : 'arrowSortDown'
              }
              className={styles.sortingIcon}
            />
          )}
        </div>
      ))}
    </div>
    <div className={styles.body}>
      {lessonGroups.map((group) => {
        const expanded =
          !showGroupHeaders || isGroupExpanded(group.id);

        return (
          <React.Fragment key={group.id}>
            {showGroupHeaders && (
              <div
                className={styles.groupHeader}
                role="button"
                tabIndex={0}
                onClick={() => onGroupHeaderClick(group.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onGroupHeaderClick(group.id);
                  }
                }}
              >
                <Icon
                  className={cn(styles.groupChevron, {
                    [styles.groupChevronExpanded]: expanded,
                  })}
                  name="chevronDown"
                />
                {group.preview !== undefined && (
                  <CoursePreview
                    className={styles.groupPreview}
                    preview={group.preview}
                  />
                )}
                <Typography
                  className={styles.groupTitle}
                  variant="body2"
                  weight="medium"
                  mode="compact"
                >
                  {group.label}
                </Typography>
                <Typography
                  className={styles.groupLessonCount}
                  variant="body3"
                  mode="compact"
                >
                  ({group.rows.length})
                </Typography>
                {group.courseType && (
                  <Typography
                    className={styles.groupCourseType}
                    variant="body3"
                    mode="compact"
                  >
                    ({group.courseType})
                  </Typography>
                )}
                {group.school && (
                  <Tooltip
                    text={group.school.name}
                    triggerClassName={styles.groupHeaderSchool}
                    triggerElement={
                      <SchoolPreview
                        className={styles.groupHeaderSchoolPreview}
                        preview={group.school.preview}
                        name={group.school.name}
                      />
                    }
                  />
                )}
              </div>
            )}
            {expanded &&
              group.rows.map((item, itemIndex) => (
                <div
                  key={item._rowKey}
                  className={styles.row}
                  onClick={() => onRowClick(item)}
                >
                  {columns.map((column) =>
                    cellRenderer(
                      {
                        ...column,
                        item,
                        itemIndex,
                        className: cellStyles.cell,
                      },
                      Cell,
                    ),
                  )}
                  {actions.length > 0 && (
                    <div className={styles.actions}>
                      {actions.map((action) => (
                        <Tooltip
                          key={action.key}
                          text={action.tooltipText}
                          triggerElement={
                            <Button
                              className={styles.action}
                              mode="icon"
                              variant="ghost"
                              size="sm"
                              iconName={action.icon}
                              onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();

                                action.onClick(item);
                              }}
                            />
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

export default React.memo(CoursesLessonsGroupedTable) as any;
